import React, { useEffect, useState } from "react";
import { auth } from "../../../utils/auth";
import { request } from "../../../utils/request";
import dayjs from "dayjs";
import Loader from "../../general/Loader";
import { formatCurrency, formatDateTime, money } from "../../../utils/helpers";
import { calcAggregate } from "./functions";

const ReporteCuadreCaja = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const id = queryParams.get("id");
    const user = auth.getUser();
    const sucursal = user.sucursales.filter(
        (suc) => suc.id === user.sucursalActual
    )[0];

    const [cuadre, setCuadre] = useState({});
    const [loading, setLoading] = useState(true);

    const getReporteData = async () => {
        setLoading(true);

        const url = `cuadre-caja/${id}`;
        const res = await request(url);
        setCuadre(res.data);
        setLoading(false);
    };

    useEffect(() => {
        getReporteData();
    }, []);

    // Espera a que loading pase a false, con los datos ya reflejados en el
    // DOM, antes de abrir el diálogo de impresión.
    useEffect(() => {
        if (!loading) {
            window.print();
        }
    }, [loading]);

    const recibos = cuadre.documentos?.filter((documento) => documento.tipo === "Recibo") ?? [];
    const facturas = cuadre.documentos?.filter((documento) => documento.tipo === "Factura") ?? [];
    const notasCreditos = cuadre.documentos?.filter((documento) => documento.tipo === "Nota de crédito") ?? [];

    const aggregate = calcAggregate(cuadre.documentos);

    return <>
		<style>{`@page {size: A4 portrait}`}</style>
        <div className="row">
            <h2 className="col-md-12 text-center m-b-5">
                Reporte Cuadre de Caja
            </h2>

            <div className="col-md-12 text-center">
                {sucursal.nombre} •{" "}
                {dayjs(cuadre.created_at).format("DD/MM/YYYY")}
            </div>

            {!loading && <div className="col-md-12 text-center">
                <strong className="mr-1">{cuadre?.vendedor ? "Vendedor:"  : "Sin Vendedor"}</strong>
                {cuadre?.vendedor ? `${cuadre.vendedor.nombre} ${cuadre.vendedor.apellido}` : ""}
            </div>}
        </div>

        {loading && <Loader/> }

        {!loading && <>
            <div className="row mt-5">
                <div className="col-8">
                    <h5 className="fw-600 w-100 text-center mb-2">
                        DETALLES DE CUADRE
                    </h5>
                    <table className="table m-b-40">
                        <thead>
                            <tr>
                                <th>Recibos</th>
                                <th>Facturas</th>
                                <th>Notas Crédito</th>
                                <th>Total Ingresos</th>
                                <th>Total Facturado</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{formatCurrency(aggregate.recibos)}</td>
                                <td>{formatCurrency(aggregate.facturas)}</td>
                                <td>{formatCurrency(aggregate.notaCreditos)}</td>
                                <td>{formatCurrency(aggregate.recibos + aggregate.facturas)}</td>
                                <td>{formatCurrency(aggregate.total_facturado)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <h5 className="fw-600 w-100 text-center mb-2">
                        FORMAS DE PAGO
                    </h5>
                    <table className="table m-b-40">
                        <thead>
                            <tr>
                                <th>Efectivo</th>
                                <th>Tarjeta</th>
                                <th>Transferencia</th>
                                <th>Depósito</th>
                                <th>Cheque</th>
                                <th>Otro</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{formatCurrency(aggregate.efectivo)}</td>
                                <td>{formatCurrency(aggregate.tarjeta)}</td>
                                <td>{formatCurrency(aggregate.transferencia)}</td>
                                <td>{formatCurrency(aggregate.deposito)}</td>
                                <td>{formatCurrency(aggregate.cheque)}</td>
                                <td>{formatCurrency(aggregate.otro)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="col-4">
                    <h5 className="fw-600 w-100 text-center mb-2">
                        TÉRMINOS DE PAGO
                    </h5>
                    <table className="table m-b-40 text-center" style={{tableLayout: 'fixed'}}>
                        <thead>
                            <tr>
                                <th>Contado</th>
                                <th>Crédito</th>
                                <th>Contra Entrega</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{formatCurrency(aggregate.contado)}</td>
                                <td>{formatCurrency(aggregate.credito)}</td>
                                <td>{formatCurrency(aggregate.contra_entrega)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {!!facturas.length && <>
                <h3>Facturas</h3>
                <table className="table m-b-15">
                    <thead>
                        <tr>
                            <th>NCF/Numero</th>
                            <th>Tipo</th>
                            <th>Cliente</th>
                            {/* <th>Vendedor</th> */}
                            <th>Creación</th>
                            <th>Término</th>
                            <th>Tipo Pago</th>
                            <th>Estado</th>
                            <th className="text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {facturas.map((factura) => {
                            return (
                                <tr key={factura.uid}>
                                    <td>{factura.numero_comprobante}</td>
                                    <td className="text-capitalize">Contado</td>
                                    <td>{factura.cliente?.codigo} - {factura.cliente?.nombre}</td>
                                    {/* <td>{factura.vendedor ? `${factura.vendedor?.nombre} - ${factura.vendedor?.apellido}` : ''}</td> */}
                                    <td>{formatDateTime(factura.created_at)}</td>
                                    <td>{factura.terminos_pago}</td>
                                    <td className="text-capitalize">{factura.tipo_pago !== 'RI' ? factura.tipo_pago : ''}</td>
                                    <td className="text-capitalize">{factura.estado}{factura.estado === 'pagada' && factura.tipo_pago === 'RI' ? ' (RI)' : ''}</td>
                                    <td className="text-right">
                                        {factura.estado === 'pendiente' || factura.tipo_pago === 'RI' ? <del>{formatCurrency(factura.total)}</del> : <span>{formatCurrency(factura.total)}</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="row m-b-40">
                    <div className="col-md-12 text-right p-b-10">
                        <div className="float-left">
                            TOTAL FACTURAS: {facturas.length}
                        </div>

                        <div className="float-right">
                            <h5 className="fw-600">
                                Total PENDIENTES: {formatCurrency(facturas.reduce((acc, f) => acc + (f.deuda - f.abono), 0))} |
                                TOTAL PAGADAS: {formatCurrency(aggregate.facturas)}
                            </h5>
                        </div>
                    </div>
                </div>
            </>}

            {!!recibos.length && <>
                <h3>Recibos de ingreso</h3>
                <table className="table m-b-15">
                    <thead>
                        <tr>
                            <th>Numero</th>
                            <th>Facturas afectadas</th>
                            <th>Cliente</th>
                            {/* <th>Vendedor</th> */}
                            <th>Creación</th>
                            <th>Deuda</th>
                            <th>Abono</th>
                            <th>Restan</th>
                            <th>Tipo de pago</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recibos.map((recibo) => {
                            return (
                                <tr key={recibo.uid}>
                                    <td>{recibo.numero_comprobante}</td>
                                    <td style={{maxWidth: '256px'}}>{recibo.referencia}</td>
                                    <td>{recibo.cliente?.codigo} - {recibo.cliente?.nombre}</td>
                                    {/* <td>{recibo.vendedor ? `${recibo.vendedor?.nombre} - ${recibo.vendedor?.apellido}` : ''}</td> */}
                                    <td>{formatDateTime(recibo.created_at)}</td>
                                    <td>{formatCurrency(recibo.deuda)}</td>
                                    <td>{formatCurrency(recibo.abono)}</td>
                                    <td>{formatCurrency(recibo.deuda - recibo.abono)}</td>
                                    <td className="text-capitalize">{recibo.tipo_pago}</td>
                                    <td className="text-capitalize">{recibo.estado}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="row m-b-40">
                    <div className="col-md-12 text-right p-b-10">
                        <div className="float-left">
                            TOTAL RECIBOS DE INGRESO: {recibos.length}
                        </div>
    
                        <div className="float-right">
                            <h5 className="fw-600">
                                VALOR TOTAL: {formatCurrency(aggregate.recibos)}
                            </h5>
                        </div>
                    </div>
                </div>
            </>}
            
            {!!notasCreditos.length && <>
                <h3>Notas de Crédito</h3>
                <table className="table m-b-15">
                    <thead>
                        <tr>
                            <th>Numero</th>
                            <th>Facturas afectadas</th>
                            <th>Cliente</th>
                            {/* <th>Vendedor</th> */}
                            <th>Creación</th>
                            <th className="text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {notasCreditos.map((nota) => {
                            return (
                                <tr key={nota.uid}>
                                    <td>{nota.numero_comprobante}</td>
                                    <td style={{maxWidth: '256px'}}>{nota.referencia}</td>
                                    <td>{nota.cliente?.codigo} - {nota.cliente?.nombre}</td>
                                    {/* <td>{nota.vendedor ? `${nota.vendedor?.nombre} - ${nota.vendedor?.apellido}` : ''}</td> */}
                                    <td>{formatDateTime(nota.created_at)}</td>
                                    <td className="text-right">{formatCurrency(nota.total)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="row m-b-40">
                    <div className="col-md-12 text-right p-b-10">
                        <div className="float-left">
                            TOTAL NOTAS DE CRÉDITO: {notasCreditos.length}
                        </div>
    
                        <div className="float-right">
                            <h5 className="fw-600">
                                VALOR TOTAL: {formatCurrency(aggregate.notaCreditos)}
                            </h5>
                        </div>
                    </div>
                </div>
            </>}
        </>}
    </>;
};

export default ReporteCuadreCaja;